import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type JobProgressCheckpoint,
  estimateJobProgressPercent,
  syncJobProgressCheckpoint,
} from '../utils/jobProgressEstimate';

export interface UseJobsApiProgressDisplayOptions {
  expectedDurationSeconds?: number;
  tickMs?: number;
}

/**
 * Smooth Jobs API progress: backend checkpoints are source of truth; exponential curve fills gaps.
 * Active jobs cap at 99% until explicit success (100%).
 */
export function useJobsApiProgressDisplay(options: UseJobsApiProgressDisplayOptions = {}) {
  const expectedDurationSeconds = Math.max(1, options.expectedDurationSeconds ?? 60);
  const tickMs = options.tickMs ?? 100;

  const [displayed, setDisplayed] = useState(0);
  const checkpointRef = useRef<JobProgressCheckpoint | null>(null);
  const succeededRef = useRef(false);

  const reset = useCallback(() => {
    checkpointRef.current = null;
    succeededRef.current = false;
    setDisplayed(0);
  }, []);

  const complete = useCallback(() => {
    succeededRef.current = true;
    checkpointRef.current = null;
    setDisplayed(100);
  }, []);

  const syncFromBackend = useCallback(
    (backendPercent: number | null | undefined) => {
      if (succeededRef.current) return;

      const result = syncJobProgressCheckpoint(
        backendPercent,
        checkpointRef.current,
        expectedDurationSeconds
      );
      if (!result) return;

      checkpointRef.current = result.checkpoint;
      setDisplayed(result.displayedPercent);
    },
    [expectedDurationSeconds]
  );

  useEffect(() => {
    const id = setInterval(() => {
      if (succeededRef.current) return;
      const checkpoint = checkpointRef.current;
      if (!checkpoint) return;
      setDisplayed(estimateJobProgressPercent(checkpoint));
    }, tickMs);

    return () => clearInterval(id);
  }, [tickMs]);

  return { displayed, syncFromBackend, complete, reset };
}
